import React from "react";
import locale from "@bonliva-crm/locale";
import { useObserver } from "mobx-react";
import DeleteIcon from "@material-ui/icons/Clear";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { ThemeType } from "@theme";
import { Body } from "@components";
import { Checkbox, Text } from "@components/formx";
import IconButton from "@material-ui/core/IconButton";
import { OnlyExperienceStore, Specialization } from "./OnlyExperienceStore";
import { ExpTopPanel, ExpMultiSelect, ExpKnownSystem, OutlinedInput } from "../Common";
import { useConsultantEditorContext } from "../../ConsultantEditorContext";

type Props = {
  profession: string;
  store: OnlyExperienceStore;
};

export function OnlyExperienceExpertise(props: Props) {
  const { profession, store: onlyExperienceStore } = props;

  const editorStore = useConsultantEditorContext();

  const profItem = `professions.${onlyExperienceStore.storeIndex}`;
  // the "specializations" error is either a message for the whole array or an
  // array of per-item errors; only the former should be rendered here
  const specializationsError = editorStore.form.getError(`${profItem}.specializations`);
  const errorMsg = !Array.isArray(specializationsError)
    ? specializationsError
    : undefined;
  const classes = useStyles({});

  return useObserver(() => (
    <>
      <Paper className={classes.info}>
        <ExpTopPanel
          certifiedPath={`${profItem}.certifiedFrom`}
          certifiedFromTxt={`${profession} sedan`}
          onDeleteClick={onlyExperienceStore.deleteExpertise}
        />

        <Body color="error" align="center">
          {errorMsg}
        </Body>

        <ExpMultiSelect
          name={locale.AddExp}
          selectOptions={onlyExperienceStore.selectSpecializationList}
          onChange={(val) => onlyExperienceStore.setSelectedSpecialization(val)}
          selectedVal={onlyExperienceStore.selectedSpecialization}
          btnClick={() => onlyExperienceStore.addSpec()}
          btnDisabled={!onlyExperienceStore.selectedSpecialization}
        />
      </Paper>

      {onlyExperienceStore.specializations.length !== 0 && (
        <ExperienceTable
          name={locale.Experienced}
          profItem={`${profItem}.specializations`}
          items={onlyExperienceStore.specializations}
          profession={profession}
          deleteRow={onlyExperienceStore.removeSpec}
        />
      )}

      <Paper style={{ padding: 16 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={6}>
            <ExpKnownSystem
              options={onlyExperienceStore.systems}
              onChange={onlyExperienceStore.addSystems}
              value={onlyExperienceStore.selectedSystems}
              hasValidated={true}
              validate={(e, c) => {
                if (c) {
                  editorStore.consultantCVManager.approveKey(
                    `professions.${onlyExperienceStore.storeIndex}.systems`
                  );
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  ));
}

type ExperienceTableProps = {
  name: string;
  profItem: string;
  profession: string;
  items: Specialization[];
  deleteRow: (path: string, i: number) => void;
};

function ExperienceTable(props: ExperienceTableProps) {
  const { items, profItem, deleteRow, profession } = props;

  return useObserver(() => (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: "15%" }}>{locale.Specialization}</TableCell>
            <TableCell align="center" style={{ width: 64 }}>
              {locale.Experienced}
            </TableCell>
            <TableCell align="center" style={{ width: "15rem" }}>
              {locale.ExperienceYearFrom}
            </TableCell>
            <TableCell align="center" style={{ width: 64, whiteSpace: "nowrap" }}>
              {locale.Interested}
            </TableCell>
            <TableCell align="center">{locale.Comment}</TableCell>
            <TableCell style={{ width: 48 }} />
          </TableRow>
        </TableHead>

        <TableBody>
          {items.map((row, i) => {
            const itemName = `${profItem}.${i}`;

            return (
              <TableRow key={itemName}>
                <TableCell style={{ fontWeight: "bold" }}>{row.name}</TableCell>
                <TableCell align="center">
                  <Checkbox name={`${itemName}.qualified`} />
                </TableCell>
                <TableCell align="center">
                  <OutlinedInput
                    name={`${itemName}.yearFrom`}
                    label={locale.ExperienceYearFrom}
                  />
                </TableCell>

                <TableCell align="center">
                  <Checkbox name={`${itemName}.interested`} />
                </TableCell>

                <TableCell align="center">
                  <Text name={`${itemName}.comment`} label={locale.WriteComment} />
                </TableCell>

                <TableCell align="center">
                  <IconButton
                    onClick={() => deleteRow(profItem, i)}
                    disabled={row.name === profession}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  ));
}

const useStyles = makeStyles((theme: ThemeType) => ({
  info: {
    ...theme.mixins.childrenMarginBottom(8),
    position: "relative",
    padding: 16
  }
}));
